/* eslint-disable @typescript-eslint/no-explicit-any */

import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators'
import store from '@/store'
import { MonitorServices } from '@/services/modules/monitor.service'
import { APIBlacklist } from '@/models/api/blacklist.model'
import { Filter } from '@/interfaces/filter.interface'
import { NotificationsServices } from '@/services/modules/notifications.service'
import { APINotification } from '@/models/api/notification.model'

@Module
export default class MonitorStore extends VuexModule {
    subscriptions: object[] = []
    subscriptionsHistory: object[] = []
    presubscriptions: object[] = []
    blacklist: object[] = []
    notification: object[] = []

    @Mutation
    updateSubscriptions (newSubscriptions: object[]) {
        this.subscriptions = newSubscriptions
    }

    @Mutation
    updateSubscriptionsHistory (newSubscriptionsHistor: object[]) {
        this.subscriptionsHistory = newSubscriptionsHistor
    }

    @Mutation
    updatePresubscriptions (newPresubscriptions: object[]) {
        this.presubscriptions = newPresubscriptions
    }

    @Mutation
    updateBlacklist (newBlacklist: object[]) {
        this.blacklist = newBlacklist
    }

    @Mutation
    updateNotification (newNotification: object[]) {
        this.notification = newNotification
    }

    @Action
    getSubscriptions (params: object) {
        return new Promise((resolve) => {
            MonitorServices.getSubscriptions(params)
                .then(resp => {
                    store.commit('updateSubscriptions', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getSubscriptionsHistory (params: {filters: Filter[]}) {
        return new Promise((resolve) => {
            if (!params.filters) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            function getDateFormat (dateString: string) {
                if (!dateString) return null

                const date = new Date(dateString)
                date.setDate(date.getDate() + 1)

                const day = date.getDate().toString().padStart(2, '0')
                const month = (date.getMonth() + 1).toString().padStart(2, '0')
                const year = date.getFullYear().toString().padStart(4, '0')
                return `${year}-${month}-${day}`
            }

            const newParams: {[key: string]: any} = {}

            params.filters.forEach(filter => {
                if (filter.type === 'date') {
                    if (filter.queryValue) {
                        if (Array.isArray(filter.queryValue)) {
                            filter.queryValue = filter.queryValue.sort((a: string, b: string) => {
                                return new Date(a).getTime() - new Date(b).getTime()
                            })
                        }

                        newParams[filter.value] = {
                            startDate: getDateFormat(filter.queryValue[0] || ''),
                            endDate: getDateFormat(filter.queryValue[1] ? filter.queryValue[1] : filter.queryValue[0] || '')
                        }
                    }
                } else {
                    newParams[filter.value] = filter.queryValue
                }
            })

            MonitorServices.getSubscriptionsHistory(newParams)
                .then(resp => {
                    store.commit('updateSubscriptionsHistory', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    exportSubscriptionsHistory (params: {filters: Filter[]}) {
        return new Promise((resolve) => {
            if (!params.filters) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            const newParams: {[key: string]: any} = {}

            params.filters.forEach(filter => {
                newParams[filter.value] = filter.queryValue
            })

            MonitorServices.exportSubscriptionsHistory(newParams)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getPresubscriptions (params: object) {
        return new Promise((resolve) => {
            MonitorServices.getPresubscriptions(params)
                .then(resp => {
                    store.commit('updatePresubscriptions', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getBlacklist (params: object) {
        return new Promise((resolve) => {
            MonitorServices.getBlacklist(params)
                .then(resp => {
                    store.commit('updateBlacklist', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorNotification (params: { page: number; filters?: Filter[]; limit: number }) {
        return new Promise((resolve) => {
            const newParams: {[key: string]: any} = { page: params.page, limit: params.limit }

            if (params.filters) {
                params.filters.forEach((filter: Filter) => {
                    if (filter.queryValue) {
                        if (!newParams[filter.value]) {
                            newParams[filter.value] = filter.queryValue
                        } else {
                            let helper: string[] = []

                            if (Array.isArray(newParams[filter.value])) {
                                helper = [ ...newParams[filter.value] ]
                            } else {
                                helper.push(newParams[filter.value])
                            }

                            if (typeof filter.queryValue === 'string') helper.push(filter.queryValue)

                            newParams[filter.value] = helper
                        }
                    }
                })
            }

            NotificationsServices.getNotificationsPaginate(newParams)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    reprocessNotification (params: APINotification) {
        return new Promise((resolve) => {
            NotificationsServices.editNotifications(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createBlacklist (params: APIBlacklist) {
        params = new APIBlacklist(params)

        return new Promise((resolve) => {
            if (!(params instanceof APIBlacklist)) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            MonitorServices.createBlacklist(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deleteBlacklist (id: number) {
        return new Promise((resolve) => {
            MonitorServices.deleteBlacklist(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorMessages (params: {filters: Filter[]}) {
        return new Promise((resolve) => {
            if (!params.filters) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            const newParams: {[key: string]: any} = {}

            function getDateFormat (dateString: string) {
                if (!dateString) return null

                const date = new Date(dateString)
                date.setDate(date.getDate() + 1)

                const day = date.getDate().toString().padStart(2, '0')
                const month = (date.getMonth() + 1).toString().padStart(2, '0')
                const year = date.getFullYear().toString().padStart(4, '0')
                return `${year}-${month}-${day}`
            }

            params.filters.forEach(filter => {
                if (filter.type === 'date') {
                    if (filter.queryValue) {
                        if (Array.isArray(filter.queryValue)) {
                            filter.queryValue = filter.queryValue.sort((a: string, b: string) => {
                                return new Date(a).getTime() - new Date(b).getTime()
                            })
                        }

                        newParams[filter.value] = {
                            startDate: getDateFormat(filter.queryValue[0] || ''),
                            endDate: getDateFormat(filter.queryValue[1] ? filter.queryValue[1] : filter.queryValue[0] || '')
                        }
                    }
                } else {
                    newParams[filter.value] = filter.queryValue
                }
            })

            MonitorServices.getMessages(newParams)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    exportMessages (params: {filters: Filter[]}) {
        return new Promise((resolve) => {
            if (!params.filters) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            const newParams: {[key: string]: any} = {}

            params.filters.forEach(filter => {
                newParams[filter.value] = filter.queryValue
            })

            MonitorServices.exportMessages(newParams)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorProviders (params: {providerId: number[]; outgoingMsisdn: string}) {
        return new Promise((resolve) => {
            if (!params.outgoingMsisdn) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            MonitorServices.getMonitorProviders(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorProvidersList (carrierId: number, providerId?: number) {
        return new Promise((resolve) => {
            if (!carrierId) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            MonitorServices.getMonitorProvidersList(carrierId, providerId)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deactivateSubscriptions (id: number) {
        return new Promise((resolve) => {
            if (!id) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            MonitorServices.deactivateSubscriptions(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getBillSubscriptions (id: number) {
        return new Promise((resolve) => {
            if (!id) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve('')
                return
            }

            MonitorServices.getBillSubscription({
                subscriptionId: id
            })
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    generateChargeSubscriptions (id: number) {
        return new Promise((resolve) => {
            if (!id) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            MonitorServices.generateChargeSubscriptions(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorProvidersFilter (params: { carrierId?: number}) {
        return new Promise((resolve) => {
            MonitorServices.getMonitorProvidersFilter(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorShortcodesFilter (params: { carrierId: number; providerId: number }) {
        return new Promise((resolve) => {
            MonitorServices.getMonitorShortcodesFilter(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorProductsFilter (params: { carrierId?: number; providerId?: number }) {
        return new Promise((resolve) => {
            MonitorServices.getMonitorProductsFilter(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getMonitorCarriersFilter () {
        return new Promise((resolve) => {
            MonitorServices.getMonitorCarriersFilter()
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }
}
